import BaseListStore from 'stores/BaseListStore';
import AgentRegistrationKeyStore, { IAgentRegistrationKey } from 'stores/agentRegistrationKeys/AgentRegistrationKeyStore';
import { SortDirection } from 'constants/SortDirection';
import { IDataFetchOptions } from '../../types';
import { getConditions, getItemListConditions, getListConditions } from 'stores/_services/filterService';
import { buildWhereFilter, destroy, getCount, getPage, post } from 'stores/_services/dataService';
import { runInAction } from 'mobx';
import { AssignType } from 'constants/AssignType';

const SEARCH_FIELDS = ['id', 'targetName'];

export default class AgentRegistrationKeyListStore extends BaseListStore<AgentRegistrationKeyStore> {
    constructor() {
        super({
            itemStore: AgentRegistrationKeyStore,
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC
        });
    }

    async getAgentRegistrationKeys(args: IDataFetchOptions = {}): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                limit: this.limit,
                skip: this.skip,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort,
                include: [
                    {
                        relation: 'user',
                        scope: {
                            fields: ['id', 'username']
                        }
                    },
                    {
                        relation: 'target',
                        scope: {
                            fields: ['data']
                        }
                    }
                ]
            }
        };

        const { data } = await getPage('agentkeydetails', options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);
    }

    async getDownloadLink({ locationId, processor }: { locationId: string, processor: string }): Promise<string> {
        const options = {
            queryParams: { locationId, processor }
        };
        const { data } = await getPage('agentkeys/downloadLink', options);
        return data;
    }

    async getTotalCount(): Promise<void> {
        const totalCount = await getCount('agentkeys', { searchFields: SEARCH_FIELDS });
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.totalCount = totalCount;
        });
    }

    async addAgentRegistrationKey(key: IAgentRegistrationKey): Promise<void> {
        await post('agentkeys', key);
    }

    async deleteAgentRegistrationKeys(itemId?: string): Promise<void> {
        const conditions = getListConditions(itemId ? [itemId] : this.selectedIdList, this.filter);

        const options = {
            where: conditions,
            searchFields: SEARCH_FIELDS
        };

        await destroy('agentkeys', options);

        this.deselectAll();

        if (itemId) {
            this.clearItem(itemId);
        }
    }

    async assignToTargetGroup(targetId: string | null, assignType: AssignType): Promise<void> {
        const conditions = this.getAssignConditions(assignType);

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('agentKeys/assignTarget', { targetId, where });

        this.deselectAll();
        this.getAgentRegistrationKeys();
    }

    private getAssignConditions(assignType: AssignType) {
        switch (assignType) {
            case 'filtered':
                return getConditions(this.filter);
            case 'selected':
                return getItemListConditions(this.selectedIdList)[0];
        }
    }
}
